import { useState, useEffect } from 'react';
import { useResetPasswordMutation, useValidateResetTokenQuery } from '../../api/authApi';
import { AlertUtils } from '../../../../shared/utils/alert';
import { useNavigate, useSearchParams } from 'react-router-dom';

export function useResetPasswordHook() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [formData, setFormData] = useState({ newPassword: '', confirmPassword: '' });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetPassword, { isLoading: isResetting }] = useResetPasswordMutation();
  const { isLoading: isValidating, error: validationError } = useValidateResetTokenQuery(token || '', {
    skip: !token
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      AlertUtils.toast('Invalid or missing reset token. Please request a new password reset.', 'error');
      navigate('/forgot-password');
    }
  }, [token, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.newPassword || !formData.confirmPassword) {
      AlertUtils.toast('Please fill in all fields', 'warning');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      AlertUtils.toast('Passwords do not match', 'warning');
      return;
    }

    // Password validation logic
    const password = formData.newPassword;
    const hasLength = password.length >= 8;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);

    if (!hasLength || !hasUpper || !hasLower || !hasNumber || !hasSpecial) {
      AlertUtils.toast('Password must be at least 8 characters long and include an uppercase letter, a lowercase letter, a number, and a special character.', 'error');
      return;
    }

    if (!token) return;

    try {
      await resetPassword({ token, new_password: formData.newPassword }).unwrap();
      AlertUtils.show('Password Reset', 'Your password has been reset successfully. You can now log in.', 'success');
      navigate('/login');
    } catch (err: any) {
      let errorMsg = err?.data?.message || err?.message || 'Failed to reset password';
      if (err?.data?.errors && Array.isArray(err.data.errors) && err.data.errors.length > 0) {
        errorMsg = err.data.errors[0].message;
      }
      AlertUtils.toast(errorMsg, 'error');
    }
  };

  return {
    formData,
    handleChange,
    isLoading: isResetting,
    handleSubmit,
    navigate,
    showNewPassword,
    setShowNewPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    isValidating,
    validationError
  };
}
